import { useState } from 'react';
import { cn } from '@/lib/cn';
import { getDocumentDownloadUrl, getDocumentFileDownloadUrl } from '@shared/api/documents';
import { getInvoiceDownloadUrl } from '@shared/api/invoices';
import { MessageTicks } from '@shared/components/message-ticks';
import { VoiceNotePlayer } from '@shared/components/voice-note-player';
import type { MessageDeliveryStatus } from '@shared/hooks/use-chat';
import { formatAdminRole, type ChatMessage, type ChatSender } from '@shared/types/api';
import { ScopeFormCard } from '@shared/components/scope-form-card';

/**
 * Admin-side bubble — mirrors the client chat's visual grammar so the same
 * conversation looks identical whichever side you're viewing from.
 * Differences from the client version:
 *   - No reply / pin / star / react actions (admin is an operator, not a
 *     participant in long-running document work).
 *   - Admin's own bubble uses a slate palette (not brand) because admin is
 *     a distinct voice in the conversation, not the client.
 *   - No MessageActions hover menu.
 *
 * Layout rules are the same as the client:
 *   SYSTEM              → centred card (no tail, no avatar, no hover).
 *   ADMIN (isMine on admin app) → right column, slate.
 *   ADMIN (from POV of seeing another admin — shouldn't happen in practice
 *                        since only one admin acts per tx, but handled)
 *                       → left column, slate, no hover card.
 *   CLIENT / ASSOCIATE / FRANCHISE → left column, hover card with name +
 *                       avatar + opaque Associate ID (for associates).
 */

type ReplyMeta = {
  id: string;
  body: string | null;
  senderName: string;
};

export type AdminBubbleProps = {
  message: ChatMessage;
  isMine: boolean;      // true when the message was sent by this admin
  showSender: boolean;  // first message of a run from this sender
  // Derived in the parent from thread-wide receipts. null for messages
  // not sent by this admin — ticks render only on own bubbles.
  deliveryStatus?: MessageDeliveryStatus | null;
  onPay?: (invoiceId: string) => void;
};

export function MessageBubble({ message, isMine, showSender, deliveryStatus, onPay }: AdminBubbleProps) {
  // Same authoritative SYSTEM detection as client — don't rely on the
  // `sender` field being present (a stale API payload could omit it).
  const isSystem =
    message.senderUserId === null &&
    message.type !== 'TEXT' &&
    message.type !== 'DOCUMENT';
  if (isSystem) return <SystemMessage message={message} onPay={onPay} />;

  const sender: ChatSender =
    message.sender ?? {
      role: 'CLIENT',
      firstName: null,
      avatarUrl: null,
      associateId: null,
    };
  const isAdminMsg = sender.role === 'ADMIN';
  const reply = (message.metadata?.replyTo as ReplyMeta | undefined) ?? null;

  // WhatsApp palette matching client & associate:
  // Admin's own messages get WhatsApp soft-green tint (#d9fdd3); counter-party gets clean white.
  const bubbleTone = isMine
    ? 'bg-[#d9fdd3] text-gray-900 shadow-sm'
    : isAdminMsg
    ? 'bg-[#f0f2f5] text-slate-900 shadow-sm'
    : 'bg-white text-gray-900 shadow-sm';

  const tailColor = isMine ? '#d9fdd3' : isAdminMsg ? '#f0f2f5' : '#ffffff';
  const showTail = showSender;

  return (
    <div className={cn('group relative flex items-start gap-2', isMine ? 'justify-end' : 'justify-start')}>
      {/* Left gutter: 28px avatar on the first message of a counter-party
          run, blank space for grouped messages so they stack under it. */}
      {!isMine ? (
        <div className="w-8 shrink-0 self-start pt-1">
          {showSender && isAdminMsg ? (
            sender.avatarUrl ? (
              <Avatar size={28} url={sender.avatarUrl} fallback={sender.firstName ?? 'A'} />
            ) : (
              <span
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-900/90 text-white text-xs font-semibold"
                aria-hidden
                title={[sender.firstName, sender.lastName].filter(Boolean).join(' ') || formatAdminRole(sender.adminRole)}
              >
                {sender.firstName ? sender.firstName.charAt(0).toUpperCase() : <ShieldGlyph />}
              </span>
            )
          ) : showSender ? (
            <Avatar size={28} url={sender.avatarUrl} fallback={sender.firstName ?? 'U'} />
          ) : null}
        </div>
      ) : null}

      <div className="relative max-w-[72%]">
        {!isMine && showSender ? <SenderHeader sender={sender} /> : null}

        <div
          className={cn(
            'relative rounded-2xl px-3 py-1.5 text-sm shadow-sm',
            bubbleTone,
            showTail && (isMine ? 'rounded-tr-none' : 'rounded-tl-none'),
          )}
        >
          {showTail ? <BubbleTail isMine={isMine} bubbleColor={tailColor} /> : null}

          {reply ? (
            <div
              className="mb-1.5 block w-full truncate rounded-md border-l-4 border-slate-400 bg-black/5 px-2 py-1 text-left text-xs text-gray-700 font-normal"
            >
              <span className="block truncate font-semibold">{reply.senderName ?? 'Reply'}</span>
              <span className="block truncate opacity-80">{reply.body ?? '(attachment)'}</span>
            </div>
          ) : null}

          {(() => {
            const isVoiceNote = Boolean(
              message.metadata?.isVoiceNote ||
                (typeof message.metadata?.mimeType === 'string' &&
                  message.metadata.mimeType.startsWith('audio/')) ||
                (typeof message.metadata?.fileName === 'string' &&
                  /\.(webm|m4a|mp4|ogg|mp3|wav|aac)$/i.test(message.metadata.fileName)),
            );
            const voiceDuration =
              typeof message.metadata?.durationSeconds === 'number'
                ? message.metadata.durationSeconds
                : undefined;

            if (isVoiceNote && message.attachmentDocumentId) {
              return (
                <div className="flex flex-col gap-1">
                  <VoiceNotePlayer
                    attachmentDocumentId={message.attachmentDocumentId}
                    durationSeconds={voiceDuration}
                    isMine={isMine}
                  />
                  {message.body ? (
                    <p className="whitespace-pre-wrap break-words px-1">{message.body}</p>
                  ) : null}
                </div>
              );
            }

            if (message.type === 'DOCUMENT') {
              return (
                <div className="flex flex-col gap-2">
                  <DocumentTile message={message} />
                  {message.body ? (
                    <p className="whitespace-pre-wrap break-words">{message.body}</p>
                  ) : null}
                </div>
              );
            }

            return <p className="whitespace-pre-wrap break-words">{message.body}</p>;
          })()}

          <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-gray-500">
            {formatTime(message.createdAt)}
            {isMine && deliveryStatus ? <MessageTicks status={deliveryStatus} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Sender header + hover card
// ------------------------------------------------------------------
function SenderHeader({ sender }: { sender: ChatSender }) {
  if (sender.role === 'ADMIN') {
    const fullName = [sender.firstName, sender.lastName].filter(Boolean).join(' ').trim();
    const roleLabel = formatAdminRole(sender.adminRole);

    return (
      <div className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-700">
        {fullName ? (
          <span className="font-semibold text-gray-900">{fullName}</span>
        ) : null}
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          <ShieldGlyph />
          {roleLabel}
        </span>
      </div>
    );
  }

  const isAssociate = sender.role === 'ASSOCIATE' || sender.role === 'FRANCHISE';
  const rawName = [sender.firstName, sender.lastName].filter(Boolean).join(' ').trim() || (isAssociate ? 'Associate' : 'Client');
  const label = rawName.charAt(0).toUpperCase() + rawName.slice(1);
  const roleLabel =
    sender.role === 'FRANCHISE'
      ? 'Franchise Associate'
      : isAssociate
      ? 'Associate'
      : 'Client';

  return (
    <div className="relative mb-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-600 [&:hover>.sender-card]:visible [&:hover>.sender-card]:opacity-100">
      {isAssociate ? (
        <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
          {roleLabel}
        </span>
      ) : (
        <span>{label}</span>
      )}

      <span
        className="sender-card pointer-events-none invisible absolute left-0 top-full z-20 mt-1 w-56 rounded-lg border border-gray-200 bg-white p-3 text-left text-xs text-gray-700 opacity-0 shadow-lg transition-opacity duration-150"
        role="tooltip"
      >
        <span className="flex items-center gap-2">
          <Avatar size={32} url={sender.avatarUrl} fallback={label} />
          <span className="min-w-0">
            <span className="block truncate font-semibold text-gray-900">{label}</span>
            <span className="block text-[10px] uppercase tracking-wide text-gray-500">
              {roleLabel}
            </span>
          </span>
        </span>
        {sender.associateId ? (
          <span className="mt-2 block text-[11px] text-gray-500">
            Associate ID{' '}
            <span className="font-mono font-semibold text-gray-800">{sender.associateId}</span>
          </span>
        ) : null}
      </span>
    </div>
  );
}

// ------------------------------------------------------------------
// Bits + bobs
// ------------------------------------------------------------------
function Avatar({
  size,
  url,
  fallback,
}: {
  size: number;
  url: string | null;
  fallback: string;
}) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-semibold uppercase text-brand-800"
      style={{ width: size, height: size }}
      aria-hidden
    >
      {fallback.slice(0, 1)}
    </span>
  );
}

function ShieldGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function BubbleTail({ isMine, bubbleColor }: { isMine: boolean; bubbleColor: string }) {
  if (isMine) {
    return (
      <svg
        className="absolute top-0 -right-[8px] h-[13px] w-[8px] z-10"
        style={{ color: bubbleColor }}
        viewBox="0 0 8 13"
        fill="currentColor"
      >
        <path d="M0 0h8v4c-1.5 1.5-3.5 3.5-8 9z" />
      </svg>
    );
  }
  return (
    <svg
      className="absolute top-0 -left-[8px] h-[13px] w-[8px] z-10"
      style={{ color: bubbleColor }}
      viewBox="0 0 8 13"
      fill="currentColor"
    >
      <path d="M8 0H0v4c1.5 1.5 3.5 3.5 8 9z" />
    </svg>
  );
}

function parseWelcomeMessage(body: string | undefined | null, metadataDocs?: unknown) {
  const text = body ?? '';
  let docs: string[] = [];

  if (Array.isArray(metadataDocs) && metadataDocs.length > 0) {
    docs = metadataDocs.map((d) => String(d).trim()).filter(Boolean);
  }

  const bulletIndex = text.indexOf('•');
  let intro = text;
  let prompt = 'Please upload the following required documents in this chat:';

  if (bulletIndex !== -1) {
    const textBeforeBullets = text.slice(0, bulletIndex).trim();
    if (docs.length === 0) {
      docs = text
        .slice(bulletIndex)
        .split('•')
        .map((s) => s.trim())
        .filter(Boolean);
    }

    const promptMatch = textBeforeBullets.match(
      /(Please upload[^\n:]*:?|upload the following[^\n:]*:?)/i,
    );
    if (promptMatch && promptMatch.index !== undefined) {
      intro = textBeforeBullets.slice(0, promptMatch.index).trim();
      prompt = textBeforeBullets.slice(promptMatch.index).trim();
    } else {
      intro = textBeforeBullets;
    }
  } else if (docs.length > 0) {
    const promptMatch = text.match(/(Please upload[^\n:]*:?|upload the following[^\n:]*:?)/i);
    if (promptMatch && promptMatch.index !== undefined) {
      intro = text.slice(0, promptMatch.index).trim();
      prompt = text.slice(promptMatch.index).trim();
    }
  }

  return { intro, prompt, docs };
}

function WelcomeSystemCard({ message }: { message: ChatMessage }) {
  const metadataDocs = message.metadata?.requiredDocuments;
  const { intro, prompt, docs } = parseWelcomeMessage(message.body, metadataDocs);

  return (
    <div className="flex justify-center my-1.5">
      <div className="max-w-lg w-full rounded-xl border border-gray-200/80 bg-white/95 px-4 py-3 text-center shadow-xs backdrop-blur-xs">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mx-auto">
          System
        </div>

        {intro ? (
          <div className="mt-1 text-sm text-gray-800 leading-relaxed font-normal">{intro}</div>
        ) : null}

        {docs.length > 0 ? (
          <div className="mt-3 rounded-lg border border-brand-200/80 bg-brand-50/60 p-3 text-left">
            <p className="text-xs font-semibold text-brand-900 mb-2">
              {prompt || 'Please upload the following required documents in this chat:'}
            </p>
            <ul className="space-y-1.5 pl-0.5">
              {docs.map((doc, idx) => (
                <li key={idx} className="flex items-center gap-2 text-xs text-brand-950">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600" />
                  <span className="font-semibold">{doc}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SystemMessage({
  message,
  onPay,
}: {
  message: ChatMessage;
  onPay?: (invoiceId: string) => void;
}) {
  const kind = message.metadata?.kind as string | undefined;
  const isQuestionnairePrompt = kind === 'questionnaire:prompt';
  const isPdfReady = kind === 'invoice:pdf_ready';
  const isDraftCard = message.type === 'DRAFT';
  const isFinalCard = message.type === 'FINAL_DOC';
  const isScopeForm =
    message.type === 'SCOPE_FORM' ||
    (kind?.startsWith('scope_form') && kind !== 'scope_form:accepted');

  if (isScopeForm) {
    return (
      <ScopeFormCard
        message={message}
        transactionId={message.transactionId}
        isAdminViewer={true}
      />
    );
  }

  const isWelcome = Boolean(
    !isQuestionnairePrompt &&
      message.body &&
      (message.body.includes('required documents') ||
        message.body.includes('Required Documents') ||
        message.body.includes('•') ||
        message.body.startsWith('Welcome to')),
  );

  if (isWelcome) {
    return <WelcomeSystemCard message={message} />;
  }

  const label =
    message.type === 'INVOICE'
      ? 'Invoice'
      : isDraftCard
      ? 'Draft'
      : isFinalCard
      ? 'Final Document'
      : isQuestionnairePrompt
      ? 'Question'
      : isPdfReady
      ? 'Invoice'
      : 'System';
  const invoiceId = (message.metadata?.invoiceId as string | undefined) ?? null;
  const paymentMode = (message.metadata?.paymentMode as string | undefined) ?? 'ONLINE';
  const isInvoiceCard = message.type === 'INVOICE';
  const isOfflineInvoice = isInvoiceCard && paymentMode === 'OFFLINE';
  const fileName = message.metadata?.fileName as string | undefined;
  const draftVersion = message.metadata?.draftVersion as number | undefined;

  const toneClass = isQuestionnairePrompt
    ? 'border-brand-200 bg-brand-50/70'
    : isDraftCard
    ? 'border-brand-200 bg-brand-50/60'
    : isFinalCard
    ? 'border-emerald-200 bg-emerald-50/60'
    : 'border-gray-200/80 bg-white/95';
  const captionClass = isQuestionnairePrompt
    ? 'text-brand-800'
    : isDraftCard
    ? 'text-brand-800'
    : isFinalCard
    ? 'text-emerald-800'
    : 'text-gray-500';

  const displayBody =
    isInvoiceCard && message.body
      ? message.body.replace(/\s*—\s*₹[\d,]+(\.\d+)?/g, '')
      : message.body;

  return (
    <div className="flex justify-center my-1">
      <div className={cn('max-w-lg rounded-xl border px-4 py-2.5 text-center shadow-xs backdrop-blur-xs', toneClass)}>
        <div className={cn('text-[11px] font-semibold uppercase tracking-wide', captionClass)}>
          {isDraftCard && draftVersion != null ? `${label} · v${draftVersion}` : label}
        </div>
        <div className="mt-1 text-sm text-gray-800 whitespace-pre-wrap">{displayBody}</div>

        {(isDraftCard || isFinalCard) && fileName ? (
          <div className="mt-2 flex items-center justify-center gap-2 rounded-md bg-black/5 px-2 py-1.5 text-xs text-gray-700">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            <span className="truncate">{fileName}</span>
          </div>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          {isInvoiceCard && invoiceId && onPay && !isOfflineInvoice ? (
            <button
              onClick={() => onPay(invoiceId)}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800"
            >
              Pay now
            </button>
          ) : null}
          {(isInvoiceCard || isPdfReady) && invoiceId ? (
            <InvoiceDownloadButton invoiceId={invoiceId} />
          ) : null}
          {isDraftCard && message.attachmentDocumentId ? (
            <DocumentDownloadButton
              documentId={message.attachmentDocumentId}
              label="Download draft"
            />
          ) : null}
          {isFinalCard && message.attachmentDocumentId ? (
            <DocumentDownloadButton
              documentId={message.attachmentDocumentId}
              label="Download document"
            />
          ) : null}
        </div>

        {isOfflineInvoice ? (
          <p className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
            This invoice is for <strong>offline payment</strong>. Admin coordinates with the
            client in this chat; record receipt via <em>Mark paid offline</em> in the action
            bar above once you&rsquo;ve seen the bank credit.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// Poll timing — 3s × 10 = ~30s tolerance. The server re-enqueues the
// render on 404, so a first click is enough to kick the pipeline even
// if the original enqueue never happened.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 10;

function InvoiceDownloadButton({ invoiceId }: { invoiceId: string }) {
  const [state, setState] = useState<'idle' | 'loading' | 'generating' | 'error'>('idle');

  async function attempt(): Promise<'opened' | 'not-ready' | 'error'> {
    try {
      const res = await getInvoiceDownloadUrl(invoiceId);
      window.open(res.url, '_blank', 'noopener');
      return 'opened';
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      return status === 404 ? 'not-ready' : 'error';
    }
  }

  async function click() {
    setState('loading');
    const first = await attempt();
    if (first === 'opened') {
      setState('idle');
      return;
    }
    if (first === 'error') {
      setState('error');
      setTimeout(() => setState('idle'), 3000);
      return;
    }
    setState('generating');
    for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      const outcome = await attempt();
      if (outcome === 'opened') {
        setState('idle');
        return;
      }
      if (outcome === 'error') {
        setState('error');
        setTimeout(() => setState('idle'), 3000);
        return;
      }
    }
    setState('idle');
  }

  const label =
    state === 'loading'
      ? 'Preparing…'
      : state === 'generating'
      ? 'Generating…'
      : state === 'error'
      ? 'Try again'
      : 'Download invoice';

  return (
    <button
      type="button"
      onClick={click}
      disabled={state === 'loading' || state === 'generating'}
      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      {label}
    </button>
  );
}

function DocumentTile({ message }: { message: ChatMessage }) {
  const name = (message.metadata?.fileName as string) ?? 'Document';
  const id = message.attachmentDocumentId;
  const [state, setState] = useState<'idle' | 'viewing' | 'downloading' | 'error'>('idle');
  async function open() {
    if (!id) return;
    setState('viewing');
    try {
      const res = await getDocumentDownloadUrl(id);
      window.open(res.url, '_blank', 'noopener');
      setState('idle');
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 3000);
    }
  }
  async function download(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (!id) return;
    setState('downloading');
    try {
      const res = await getDocumentFileDownloadUrl(id);
      window.open(res.url, '_blank', 'noopener');
      setState('idle');
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 3000);
    }
  }
  return (
    <div className="flex w-full items-center gap-2 rounded-md bg-black/5 px-2 py-1.5">
      <button
        type="button"
        onClick={open}
        disabled={state === 'viewing' || state === 'downloading'}
        className="flex min-w-0 flex-1 items-center gap-2 text-left hover:opacity-70 disabled:opacity-60"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
        <span className="truncate text-sm">
          {state === 'viewing' ? 'Opening…' : state === 'error' ? 'Retry' : name}
        </span>
      </button>
      <button
        type="button"
        onClick={download}
        disabled={!id || state === 'viewing' || state === 'downloading'}
        className="inline-flex shrink-0 items-center justify-center rounded border border-gray-300 bg-white p-1.5 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        aria-label={`Download ${name}`}
        title={`Download ${name}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </button>
    </div>
  );
}

function DocumentDownloadButton({
  documentId,
  label,
}: {
  documentId: string;
  label: string;
}) {
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  async function open() {
    setState('loading');
    try {
      const res = await getDocumentDownloadUrl(documentId);
      window.open(res.url, '_blank', 'noopener');
      setState('idle');
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 3000);
    }
  }
  return (
    <button
      type="button"
      onClick={open}
      disabled={state === 'loading'}
      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      {state === 'loading' ? 'Opening…' : state === 'error' ? 'Retry' : label}
    </button>
  );
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
